import React, { useMemo, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Folder, Layers, Network, Activity, Diamond } from 'lucide-react';

const ZOOMS = { Day: 30, Week: 12, Month: 4 }; // px per day
const ROW_H = 34;
const LABEL_W = 280;
const DAY_MS = 86400000;

const parseDate = (s) => (s ? new Date(`${s}T00:00:00`) : null);
const diffDays = (a, b) => Math.round((b - a) / DAY_MS);
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const TYPE_ICON = {
  PROJECT: Folder,
  WBS: Layers,
  NETWORK: Network,
  ACTIVITY: Activity,
  MILESTONE: Diamond,
};

const barColor = (item) => {
  if (item.is_critical_path) return 'bg-red-500';
  if (item.type === 'NETWORK' || item.type === 'ACTIVITY') return 'bg-emerald-500';
  if (item.type === 'PROJECT') return 'bg-orange-600';
  return 'bg-orange-400';
};

const isoAddDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const RESCHEDULABLE = new Set(['WBS', 'ACTIVITY', 'NETWORK', 'MILESTONE']);

export default function ProjectGantt({ data, onReschedule }) {
  const [zoom, setZoom] = useState('Week');
  const [drag, setDrag] = useState(null); // { id, startX, dayDelta }
  const pxPerDay = ZOOMS[zoom];

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      setDrag((d) => (d ? { ...d, dayDelta: Math.round((e.clientX - d.startX) / pxPerDay) } : d));
    };
    const onUp = () => {
      setDrag((d) => {
        if (d && d.dayDelta !== 0 && onReschedule) {
          const item = (data.items || []).find((it) => it.id === d.id);
          if (item) {
            const s = item.planned_start || item.actual_start;
            const f = item.planned_finish || item.actual_finish || s;
            if (s) onReschedule(item, isoAddDays(s, d.dayDelta), isoAddDays(f, d.dayDelta));
          }
        }
        return null;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [drag, pxPerDay, data.items, onReschedule]);

  const { start, end, totalDays } = useMemo(() => {
    const items = data.items || [];
    let lo = parseDate(data.earliest_start);
    let hi = parseDate(data.latest_finish);
    items.forEach((it) => {
      const s = parseDate(it.planned_start || it.actual_start);
      const f = parseDate(it.planned_finish || it.actual_finish || it.planned_start);
      if (s && (!lo || s < lo)) lo = s;
      if (f && (!hi || f > hi)) hi = f;
    });
    if (!lo) lo = new Date();
    if (!hi || hi < lo) hi = addDays(lo, 30);
    const padStart = addDays(lo, -3);
    const padEnd = addDays(hi, 3);
    return { start: padStart, end: padEnd, totalDays: Math.max(diffDays(padStart, padEnd) + 1, 1) };
  }, [data]);

  const chartWidth = totalDays * pxPerDay;

  const months = useMemo(() => {
    const segs = [];
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
      const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const segStart = cur < start ? start : cur;
      const segEnd = next > addDays(end, 1) ? addDays(end, 1) : next;
      segs.push({
        key: `${cur.getFullYear()}-${cur.getMonth()}`,
        label: cur.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        left: diffDays(start, segStart) * pxPerDay,
        width: Math.max(diffDays(segStart, segEnd) * pxPerDay, 0),
      });
      cur = next;
    }
    return segs;
  }, [start, end, pxPerDay]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayLeft = today >= start && today <= end ? diffDays(start, today) * pxPerDay : null;

  const items = data.items || [];

  const barGeom = (item) => {
    const s = parseDate(item.planned_start || item.actual_start);
    const f = parseDate(item.planned_finish || item.actual_finish || item.planned_start);
    if (!s) return null;
    const left = diffDays(start, s) * pxPerDay;
    const days = f ? diffDays(s, f) + 1 : 1;
    const width = Math.max(days * pxPerDay, 6);
    return { left, width };
  };

  return (
    <div className="space-y-3" data-testid="project-gantt">
      {/* Toolbar + legend */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-orange-500 inline-block" /> WBS / Project</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> Activity / Network</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500 inline-block" /> Critical path</span>
          <span className="flex items-center gap-1"><Diamond className="w-3 h-3 text-amber-500 fill-amber-500" /> Milestone</span>
        </div>
        <div className="flex items-center gap-1 border rounded-md p-0.5 bg-stone-50">
          {Object.keys(ZOOMS).map((z) => (
            <Button
              key={z}
              size="sm"
              variant={zoom === z ? 'default' : 'ghost'}
              className={`h-7 px-3 text-xs ${zoom === z ? 'bg-stone-800 hover:bg-stone-700' : ''}`}
              onClick={() => setZoom(z)}
              data-testid={`gantt-zoom-${z.toLowerCase()}`}
            >
              {z}
            </Button>
          ))}
        </div>
      </div>

      {/* Gantt grid */}
      <div className="border rounded-lg overflow-x-auto bg-white" data-testid="gantt-scroll">
        <div style={{ width: LABEL_W + chartWidth, minWidth: '100%' }}>
          {/* Header */}
          <div className="flex sticky top-0 z-20 bg-stone-100 border-b">
            <div
              className="sticky left-0 z-30 bg-stone-100 border-r font-semibold text-xs text-stone-600 flex items-center px-3"
              style={{ width: LABEL_W, minWidth: LABEL_W, height: ROW_H }}
            >
              Task
            </div>
            <div className="relative" style={{ width: chartWidth, height: ROW_H }}>
              {months.map((m) => (
                <div
                  key={m.key}
                  className="absolute top-0 h-full border-r border-stone-200 text-[11px] text-stone-500 px-1 flex items-center overflow-hidden"
                  style={{ left: m.left, width: m.width }}
                >
                  {m.label}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          <div className="relative">
            {/* today line spanning all rows */}
            {todayLeft != null && (
              <div
                className="absolute top-0 bottom-0 w-px bg-red-400 z-10"
                style={{ left: LABEL_W + todayLeft }}
                title={`Today: ${today.toISOString().slice(0, 10)}`}
              />
            )}
            {items.map((item) => {
              const Icon = TYPE_ICON[item.type] || Activity;
              const geom = barGeom(item);
              const canDrag = !!onReschedule && RESCHEDULABLE.has(item.type);
              const dragging = drag && drag.id === item.id;
              const dragPx = dragging ? drag.dayDelta * pxPerDay : 0;
              const startDrag = (e) => {
                if (!canDrag) return;
                e.preventDefault();
                e.stopPropagation();
                setDrag({ id: item.id, startX: e.clientX, dayDelta: 0 });
              };
              return (
                <div key={item.id} className="flex border-b last:border-b-0 hover:bg-stone-50" data-testid={`gantt-row-${item.id}`}>
                  {/* Label */}
                  <div
                    className="sticky left-0 z-10 bg-white border-r flex items-center gap-1.5 px-3 truncate"
                    style={{ width: LABEL_W, minWidth: LABEL_W, height: ROW_H, paddingLeft: 12 + (item.level || 0) * 16 }}
                  >
                    <Icon className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                    <span className="text-sm text-stone-700 truncate" title={item.name}>{item.name}</span>
                  </div>
                  {/* Timeline */}
                  <div className="relative" style={{ width: chartWidth, height: ROW_H }}>
                    {geom && item.is_milestone ? (
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-amber-500 rotate-45 border border-amber-600 ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''} ${dragging ? 'ring-2 ring-amber-400' : ''}`}
                        style={{ left: geom.left - 6 + dragPx }}
                        onPointerDown={startDrag}
                        title={`${item.name} • ${item.planned_finish || item.planned_start || ''}${canDrag ? '\n(drag to reschedule)' : ''}`}
                      />
                    ) : geom ? (
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 h-4 rounded ${barColor(item)} shadow-sm overflow-hidden ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''} ${dragging ? 'ring-2 ring-orange-400' : ''}`}
                        style={{ left: geom.left + dragPx, width: geom.width }}
                        onPointerDown={startDrag}
                        data-testid={`gantt-bar-${item.id}`}
                        title={`${item.name}\n${item.planned_start || '?'} → ${item.planned_finish || '?'}\nProgress: ${item.progress || 0}%${item.is_critical_path ? '\n(Critical path)' : ''}${canDrag ? '\n(drag to reschedule)' : ''}`}
                      >
                        {item.progress > 0 && (
                          <div className="h-full bg-black/25" style={{ width: `${Math.min(item.progress, 100)}%` }} />
                        )}
                      </div>
                    ) : (
                      <span className="absolute top-1/2 -translate-y-1/2 left-2 text-[11px] text-stone-300">no dates</span>
                    )}
                  </div>
                </div>
              );
            })}
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-stone-400">No timeline items for this project.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
